import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { CustomErrorPage } from '../CustomErrorPage';
import { DocumentationTab } from './api/types';
import { DocsLayout } from './components/DocsLayout';
import { HtmlDoc } from './components/HtmlDoc/HtmlDoc';
import { useDocumentationItem } from './hooks/useDocumentationItem';
import { useDocumentationTabs } from './hooks/useDocumentationTabs';
import {
  ArticleContentWrapper,
  BackButton,
  DocsContent,
  ParagraphText,
} from './styles';
import { getDocumentationReturnPath } from './utils/documentationNavigationState';
import {
  getDocumentationRoute,
  isDocumentTagWithinTab,
  mapDocumentationTabToDocsTabItem,
  normalizeDocumentTagName,
} from './utils/documentationRoutes';
import { getDocumentationErrorStatus } from './utils/getDocumentationErrorStatus';

function DocsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { '*': documentTagPath } = useParams<{ '*': string }>();
  const documentTagName = useMemo(
    () => normalizeDocumentTagName(documentTagPath),
    [documentTagPath],
  );
  const tabsQuery = useDocumentationTabs();
  const documentationTabs = useMemo(
    () => tabsQuery.data ?? [],
    [tabsQuery.data],
  );
  const docsTabs = documentationTabs.map(mapDocumentationTabToDocsTabItem);
  const matchingTabIndex = findMatchingDocumentationTabIndex(
    documentationTabs,
    documentTagName,
  );
  const currentTabIndex = Math.max(0, matchingTabIndex);
  const selectedTab = documentationTabs[currentTabIndex];
  const selectedDocumentTagName =
    documentTagName && matchingTabIndex >= 0 ? documentTagName : undefined;
  const documentQuery = useDocumentationItem(selectedDocumentTagName);

  useEffect(() => {
    if (
      tabsQuery.isLoading ||
      tabsQuery.isError ||
      documentationTabs.length === 0
    ) {
      return;
    }

    if (!documentTagName || matchingTabIndex < 0) {
      navigate(getDocumentationRoute(documentationTabs[0].documentTagName), {
        replace: true,
      });
    }
  }, [
    documentationTabs,
    documentTagName,
    matchingTabIndex,
    navigate,
    tabsQuery.isError,
    tabsQuery.isLoading,
  ]);

  function handleTabChange(
    _event: React.ChangeEvent<{}>,
    nextTabIndex: number,
  ) {
    const nextTab = documentationTabs[nextTabIndex];

    if (nextTab) {
      navigate(getDocumentationRoute(nextTab.documentTagName));
    }
  }

  function handleNestedDocumentBackClick() {
    const documentationReturnPath = getDocumentationReturnPath(
      location.state,
      location,
    );

    if (documentationReturnPath) {
      navigate(-1);
      return;
    }

    if (selectedTab) {
      navigate(getDocumentationRoute(selectedTab.documentTagName), {
        replace: true,
      });
    }
  }

  if (tabsQuery.isError) {
    const { status, statusMessage } = getDocumentationErrorStatus(
      tabsQuery.error,
    );

    return <CustomErrorPage status={status} statusMessage={statusMessage} />;
  }

  if (tabsQuery.isLoading) {
    return (
      <DocsLayout tabs={[]} currentTab={0} onTabChange={handleTabChange}>
        <Progress />
      </DocsLayout>
    );
  }

  if (documentationTabs.length === 0) {
    return (
      <DocsLayout tabs={[]} currentTab={0} onTabChange={handleTabChange}>
        <ParagraphText>No documentation is available.</ParagraphText>
      </DocsLayout>
    );
  }

  const isNestedDocument =
    Boolean(selectedTab) &&
    documentTagName !== normalizeDocumentTagName(selectedTab.documentTagName);

  return (
    <DocsLayout
      tabs={docsTabs}
      currentTab={currentTabIndex}
      onTabChange={handleTabChange}
      isSubPage={isNestedDocument}
    >
      <DocsContent>
        <ArticleContentWrapper>
          {isNestedDocument ? (
            <BackButton
              startIcon={<ArrowBackIcon />}
              color="primary"
              onClick={handleNestedDocumentBackClick}
            >
              Back
            </BackButton>
          ) : null}
          {documentQuery.isLoading ? (
            <ParagraphText>Loading article...</ParagraphText>
          ) : null}
          {documentQuery.isError ? (
            <ParagraphText>
              Unable to load this article. Please try again later.
            </ParagraphText>
          ) : null}
          {documentQuery.data ? (
            <HtmlDoc
              html={documentQuery.data.html}
              title={documentQuery.data.title}
            />
          ) : null}
        </ArticleContentWrapper>
      </DocsContent>
    </DocsLayout>
  );
}

function findMatchingDocumentationTabIndex(
  documentationTabs: DocumentationTab[],
  documentTagName: string,
): number {
  let matchingTabIndex = -1;
  let matchingTagLength = -1;

  documentationTabs.forEach((documentationTab, tabIndex) => {
    const tabDocumentTagName = normalizeDocumentTagName(
      documentationTab.documentTagName,
    );

    if (
      isDocumentTagWithinTab(documentTagName, tabDocumentTagName) &&
      tabDocumentTagName.length > matchingTagLength
    ) {
      matchingTabIndex = tabIndex;
      matchingTagLength = tabDocumentTagName.length;
    }
  });

  return matchingTabIndex;
}

export default DocsPage;
